/**
 * Pure helpers for the selection step: how sheets are grouped, filtered and costed.
 */
import type { Sheet, SheetRole } from "@/api/client";

export const ROLE_ORDER: readonly SheetRole[] = [
  "elevation",
  "enlarged_elevation",
  "plan",
  "section",
  "schedule",
  "other",
];

export const ROLE_LABEL: Record<SheetRole, string> = {
  elevation: "Elevations",
  enlarged_elevation: "Enlarged elevations",
  plan: "Plans",
  section: "Sections",
  schedule: "Schedules",
  other: "Other sheets",
};

export const ROLE_ONE: Record<SheetRole, string> = {
  elevation: "Elevation",
  enlarged_elevation: "Enlarged elevation",
  plan: "Plan",
  section: "Section",
  schedule: "Schedule",
  other: "Other",
};

/** About what one sheet costs to classify. An estimate, and labelled as one. */
export const COST_PER_SHEET_USD = 0.05;

export type SheetFilter = "all" | "selected" | "suggested" | SheetRole;

const normalise = (text: string) => text.toLowerCase().replace(/[\s.\-_]/g, "");

/** Sheets that pass the filter chip and the search box, in page order. */
export function filterSheets(
  sheets: readonly Sheet[],
  filter: SheetFilter,
  search: string,
  selected: ReadonlySet<number>,
): Sheet[] {
  const needle = normalise(search);
  return sheets.filter((sheet) => {
    if (filter === "selected" && !selected.has(sheet.page_index)) {
      return false;
    }
    if (filter === "suggested" && !sheet.suggested) {
      return false;
    }
    if (filter !== "all" && filter !== "selected" && filter !== "suggested") {
      if (sheet.role !== filter) {
        return false;
      }
    }
    if (!needle) {
      return true;
    }
    const hay = normalise(`${sheet.sheet_number ?? ""} ${sheet.sheet_title ?? ""}`);
    return hay.includes(needle);
  });
}

export interface SheetGroup {
  key: string;
  title: string;
  sheets: Sheet[];
}

/** The suggested sheets first as one group, then everything else by role. */
export function groupSheets(sheets: readonly Sheet[]): SheetGroup[] {
  const groups: SheetGroup[] = [];
  const suggested = sheets.filter((sheet) => sheet.suggested);
  if (suggested.length) {
    groups.push({ key: "suggested", title: "Suggested for the take-off", sheets: suggested });
  }
  for (const role of ROLE_ORDER) {
    const rest = sheets.filter((sheet) => !sheet.suggested && sheet.role === role);
    if (rest.length) {
      groups.push({ key: role, title: ROLE_LABEL[role], sheets: rest });
    }
  }
  return groups;
}

/** What the selected sheets will cost to classify; sheets already classified are free. */
export function estimateCost(
  selected: ReadonlySet<number>,
  classified: ReadonlySet<number>,
): { sheets: number; usd: number } {
  let sheets = 0;
  for (const index of selected) {
    if (!classified.has(index)) {
      sheets += 1;
    }
  }
  return { sheets, usd: sheets * COST_PER_SHEET_USD };
}

/** "A5.02" when the sheet has a number, "Sheet 51" when it does not. */
export function sheetName(sheet: { sheet_number: string | null; page_index: number }): string {
  return sheet.sheet_number ?? `Sheet ${sheet.page_index + 1}`;
}
